// Updated CompletedServiceOrders.tsx with server-side filtering
import { useMemo, useState, useCallback, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Input } from "@shared/ui/input";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { GenericTable } from "@shared/components/GenericTable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";
import {
  UserCheck,
  CheckCircle,
  Clock,
  Search,
  Filter,
  Printer,
  Zap,
  User,
  Settings,
  ChevronDown,
  TrendingUp,
  AlertTriangle,
  Eye,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
// Updated imports - using new filtering hooks
import {
  useCompletedServiceOrdersExport,
  useCompletedServiceOrdersWithFilters,
  useCompletedSummary,
  useDownloadReport,
} from "../../hooks";
import { serviceOrderFilters, TransformedServiceOrder } from "../../types";

// Import existing components
import { ServiceOrderFilterPanel } from "../../../dispatcher/components/filters/filters";

import {
  getPriorityColor,
  getStatusColor,
  formatDate,
  formatDateTime,
  getAssociationBadgeClass,
} from "../../../utils";
import { toast } from "@shared/hooks/use-toast";

// Utility functions
const getTimeTakenColorClass = (performance: string) => {
  switch (performance) {
    case "excellent":
      return "text-green-600 font-semibold";
    case "good":
      return "text-blue-600 font-medium";
    case "average":
      return "text-orange-600 font-medium";
    case "poor":
      return "text-red-600 font-semibold";
    default:
      return "text-gray-600";
  }
};

const getAssociationIcon = (association: string) => {
  const assocLower = association?.toLowerCase();
  switch (assocLower) {
    case "meter":
      return <Zap className="h-4 w-4" />;
    case "consumer":
      return <User className="h-4 w-4" />;
    case "asset":
      return <Settings className="h-4 w-4" />;
    default:
      return null;
  }
};

const formatTimeTaken = (timeTaken: any) => {
  if (!timeTaken) return "N/A";

  if (typeof timeTaken === "object") {
    if (timeTaken.hours >= 24) {
      const days = Math.floor(timeTaken.hours / 24);
      const remainingHours = timeTaken.hours % 24;
      return `${days}d ${remainingHours}h`;
    }
    return `${timeTaken.hours}h ${timeTaken.minutes}m`;
  }

  return timeTaken.toString();
};

export function CompletedServiceOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // =================== API DATA FETCHING WITH FILTERS ===================
  const {
    serviceOrders,
    isLoading,
    error,
    pagination,
    currentFilters,
    handleSearch,
    handlePageChange,
    handleApplyFilters,
    handleResetFilters,
    refetch,
  } = useCompletedServiceOrdersWithFilters();
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError,
  } = useCompletedSummary();
  const downloadCSV = useDownloadReport();

  const { exportCSV, isExporting } =
    useCompletedServiceOrdersExport(currentFilters);

  const handleExport = () => {
    exportCSV();
  };

  useEffect(() => {
    refetch();
  }, [location.pathname]);
  // Parse current filter values from URL for display
  const searchQuery = searchParams.get("query") || "";
  const statusFilter = searchParams.get("status") || "all";
  const serviceAssociationFilter =
    searchParams.get("serviceAssociation") || "all";
  const priorityFilter = searchParams.get("priority") || "all";
  const areaFilter = searchParams.get("area") || "all";
  const slaFilter = searchParams.get("sla") || "all";
  const performanceFilter = searchParams.get("performance") || "all";

  // State for custom filters (keeping backward compatibility)
  const [selectedAssociations, setSelectedAssociations] = useState([
    "meter",
    "consumer",
    "asset",
  ]);
  const [selectedSONames, setSelectedSONames] = useState<string[]>([]);

  // =================== KPI CALCULATIONS ===================
  const kpis = useMemo(() => {
    return {
      totalCompleted: summaryData?.completed || 0,
      totalRejected: summaryData?.rejected || 0,
      totalApproved: summaryData?.approved || 0,
      averageTime: summaryData?.completionTime || 0, // Calculate based on actual data if available
    };
  }, [summaryData]);
  const completedServiceOrderKpiCards = [
    {
      title: "Total Completed Orders",
      count: kpis.totalCompleted,
      trendSummary: "All successfully completed service orders",
      trendDirection: "up" as const,
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
    },
    {
      title: "Total Rejected Orders",
      count: kpis.totalRejected,
      trendSummary: "Orders rejected during completion process",
      trendDirection: "down" as const,
      icon: <UserCheck className="h-8 w-8 text-orange-500" />,
    },
    {
      title: "Total Approved Orders",
      count: kpis.totalApproved,
      trendSummary: "Orders approved and successfully completed",
      trendDirection: "up" as const,
      icon: <CheckCircle className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Average Completion Time",
      count: `${kpis.averageTime}h`,
      trendSummary: "Average time taken to complete service orders",
      trendDirection: "down" as const,
      icon: <Clock className="h-8 w-8 text-purple-500" />,
    },
  ];
  // =================== EVENT HANDLERS ===================
  const handleViewClick = (order: any) => {
    const association = order.serviceAssociation?.toLowerCase();

    switch (association) {
      case "meter":
        navigate(
          `/service-orders/dispatcher/completed-meter-service-order/${order.id}`
        );
        break;
      case "consumer":
        navigate(
          `/service-orders/dispatcher/completed-consumer-service-order/${order.id}`
        );
        break;
      case "asset":
        navigate(
          `/service-orders/dispatcher/completed-asset-service-order/Asset/${order.id}`
        );
        break;
      case "facility":
        navigate(
          `/service-orders/dispatcher/completed-asset-service-order/Facility/${order.id}`
        );
        break;
      case "system":
        navigate(
          `/service-orders/dispatcher/completed-asset-service-order/System/${order.id}`
        );
        break;
      case "network":
        navigate(
          `/service-orders/dispatcher/completed-asset-service-order/Network/${order.id}`
        );
        break;
      default:
        navigate(
          `/service-orders/dispatcher/completed-service-order/${order.id}`
        );
        break;
    }
  };

  // =================== TABLE CONFIGURATION ===================
  const tableColumns = [
    {
      key: "soId",
      header: "SO ID",
      sortable: true,
      render: (value: string) => (
        <span className="text-blue-600 font-medium">{value}</span>
      ),
    },
    {
      key: "soName",
      header: "SO Name",
      sortable: true,
      render: (value: string) => (
        <span
          className="font-medium text-gray-900 max-w-[250px] truncate block"
          title={value}
        >
          {value}
        </span>
      ),
    },
    {
      key: "referenceNo",
      header: "Reference No.",
      render: (value: string) => (
        <span className="font-mono text-gray-700">{value}</span>
      ),
    },
    {
      key: "technician",
      header: "Technician",
      sortable: true,
      render: (value: any) => {
        if (!value) return <span className="text-gray-500">Unassigned</span>;
        if (typeof value === "string")
          return <span className="font-medium text-gray-900">{value}</span>;
        return (
          <div>
            <div className="font-medium text-gray-900">
              {value.name || "Unassigned"}
            </div>
            <div className="text-sm text-gray-500">({value.id || "N/A"})</div>
          </div>
        );
      },
    },
    {
      key: "serviceAssociation",
      header: "Service Association",
      sortable: true,
      render: (value: string) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {getAssociationIcon(value)}
          <span className="ml-1">{value}</span>
        </span>
      ),
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => getStatusColor(value),
      },
    },
    {
      key: "slaStatus",
      header: "SLA Compliance",
      sortable: true,
      render: (value: string) => {
        return value || "NA";
      },
    },

    {
      key: "createdOn",
      header: "Created On",
      sortable: true,
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "assignDate",
      header: "Assigned On",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ? value : "-"}</span>
      ),
    },
    {
      key: "completedDate",
      header: "Completed On",
      sortable: true,
      render: (value: string) => (
        <span className="text-gray-700">{value ? value : "-"}</span>
      ),
    },
    {
      key: "completedTime",
      header: "Time Taken",
      sortable: true,
      render: (value: any) => (
        <span className={getTimeTakenColorClass(value?.performance || "")}>
          {formatTimeTaken(value)}
        </span>
      ),
    },
  ];

  // GenericTable actions configuration
  const tableActions = [
    {
      label: "View Details",
      onClick: (row: any) => handleViewClick(row),
      icon: "Eye" as const,
      variant: "ghost" as const,
    },
    {
      label: "Print Report",
      onClick: (order: TransformedServiceOrder) =>
        downloadCSV.mutateAsync({ reportId: order.id, tabName: "completed" }),
      icon: "Printer" as const,
      variant: "ghost" as const,
    },
  ];

  // Sorting configuration
  const [sortConfig, setSortConfig] = useState({
    field: "completedOn",
    direction: "desc" as "asc" | "desc",
  });

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
  };

  useEffect(() => {
    if (downloadCSV.isPending) {
      toast({
        title: "Report Downloading",
        description: "Your service order report is downloading.",
        variant: "default",
        duration: 3000,
      });
    }
    if (downloadCSV.isSuccess) {
      toast({
        title: "Report Downloaded Successfully",
        description: "Your service order report has been downloaded.",
        variant: "default",
        duration: 3000,
      });
    }

    if (downloadCSV.isError) {
      toast({
        title: "Download Failed",
        description:
          downloadCSV.error?.message ||
          "Failed to download report. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  }, [downloadCSV.isSuccess, downloadCSV.isError, downloadCSV.isPending]);
  // Export functionality

  // Create current filters object for filterPanel
  const filterPanelFilters: Partial<serviceOrderFilters> = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      serviceAssociation:
        serviceAssociationFilter !== "all"
          ? serviceAssociationFilter
          : undefined,
      priority: priorityFilter !== "all" ? priorityFilter : undefined,
      area: areaFilter !== "all" ? areaFilter : undefined,
      sla_filter: slaFilter !== "all" ? slaFilter : undefined,
      performance: performanceFilter !== "all" ? performanceFilter : undefined,
      search_data: searchQuery || undefined,
    }),
    [
      statusFilter,
      serviceAssociationFilter,
      priorityFilter,
      areaFilter,
      slaFilter,
      performanceFilter,
      searchQuery,
    ]
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="page-subheading">Completed Service Orders</h2>
        <p className="page-subtitle">
          Review and analyze completed service operations
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {completedServiceOrderKpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Service Orders Table */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Completed Service Orders ({pagination?.totalItems || 0})
        </h3>

        <GenericTable
          data={serviceOrders}
          columns={tableColumns}
          actions={tableActions}
          actionsType="dropdown"
          rowKey="soId"
          sortConfig={sortConfig}
          onSort={handleSort}
          loading={isLoading}
          // Search integration like UserTable
          onSearch={handleSearch}
          searchPlaceholder="Search completed orders by SO ID, Name..."
          searchParamName="query"
          searchDebounceMs={300}
          // Filter panel integration like UserTable
          filterPanel={
            <ServiceOrderFilterPanel
              currentFilters={filterPanelFilters}
              onApplyFilters={handleApplyFilters}
              onResetFilters={handleResetFilters}
              orderType="completed"
            />
          }
          // Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          onExport={handleExport} // Add this line
          exportLoading={isExporting}
          emptyMessage="No completed service orders found matching your criteria"
        />
      </div>
    </div>
  );
}
